/**
 * Calendar Event Types
 *
 * Types for calendar events (vehicle delivery/return scheduling).
 * Maps to BE: io.alture.eurodrive.entities.calendarevents.CalendarEvent
 */

// ==================== Enums ====================

/**
 * Calendar event status/type
 * Maps to BE: io.alture.eurodrive.entities.calendarevents.CalendarEventStatus
 */
export type CalendarEventStatus = 'RENT' | 'RETURN'

/**
 * Frontend event type (lowercase for UI)
 */
export type CalendarEventType = 'delivery' | 'return'

// ==================== Entities ====================

/**
 * Calendar event entity from BE
 */
export interface CalendarEvent {
  id: string
  rentalCar: string | null      // UUID reference to rental car
  damagedCar: string | null     // UUID reference to damaged car
  customer: string | null       // Customer name (String)
  eventDate: number             // timestamp (milliseconds)
  location: string | null
  calendarEvent: CalendarEventStatus
  insurance: string | null
  customData: CalendarEventCustomData
}

/**
 * Custom data stored with calendar event
 */
export interface CalendarEventCustomData {
  endDate?: number              // End timestamp for event duration
  [key: string]: unknown
}

// ==================== Frontend Types ====================

/**
 * Frontend form data for creating/editing events
 */
export interface CalendarEventForm {
  type: CalendarEventType
  date: string              // YYYY-MM-DD
  time: string              // HH:mm
  location: string
  customerName: string
  damagedCar: string        // Display text (plate or description)
  rentedCar: string         // Display text (plate or description)
  insurance: string
}

/**
 * FullCalendar event format for display
 */
export interface FullCalendarEvent {
  id: string
  title: string
  start: string             // ISO date string
  end: string               // ISO date string
  backgroundColor: string
  borderColor: string
  extendedProps: {
    type: CalendarEventType
    location?: string
    customerName?: string
    damagedCar?: string
    rentedCar?: string
    insurance?: string
  }
}

// ==================== Constants ====================

/**
 * Event type configuration with colors
 */
export const EVENT_TYPE_CONFIG = {
  delivery: {
    title: 'Přistavení auta',
    backgroundColor: '#4CAF50',
    borderColor: '#388E3C'
  },
  return: {
    title: 'Vrácení auta',
    backgroundColor: '#2196F3',
    borderColor: '#1976D2'
  }
} as const

// ==================== Helpers ====================

/**
 * Format Date to YYYY-MM-DD string (for date inputs)
 */
export function toDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format Date to HH:mm string (for time inputs)
 */
function toTimeString(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

// ==================== Mapping Functions ====================

/**
 * Map BE status to frontend type
 * RENT -> delivery, RETURN -> return
 */
export function mapStatusToFrontend(status: CalendarEventStatus): CalendarEventType {
  return status === 'RENT' ? 'delivery' : 'return'
}

/**
 * Map frontend type to BE status
 * delivery -> RENT, return -> RETURN
 */
export function mapStatusToBackend(type: CalendarEventType): CalendarEventStatus {
  return type === 'delivery' ? 'RENT' : 'RETURN'
}

/**
 * Convert CalendarEvent to FullCalendar format
 */
export function mapToFullCalendarEvent(event: CalendarEvent): FullCalendarEvent {
  const type = mapStatusToFrontend(event.calendarEvent)
  const config = EVENT_TYPE_CONFIG[type]
  const customData = event.customData || {}

  // Build title: "Type - CustomerName" or just "Type"
  const title = event.customer
    ? `${config.title} - ${event.customer}`
    : config.title

  // Start date from eventDate
  const startDate = new Date(event.eventDate)

  // End date: use customData.endDate or default to 1 hour later
  const endDate = customData.endDate
    ? new Date(customData.endDate)
    : new Date(event.eventDate + 60 * 60 * 1000)

  return {
    id: event.id,
    title,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    backgroundColor: config.backgroundColor,
    borderColor: config.borderColor,
    extendedProps: {
      type,
      location: event.location || undefined,
      customerName: event.customer || undefined,
      damagedCar: event.damagedCar || undefined,
      rentedCar: event.rentalCar || undefined,
      insurance: event.insurance || undefined
    }
  }
}

/**
 * Convert form data to API parameters
 */
export function mapFormToApiParams(form: CalendarEventForm): {
  eventDate: number
  location: string | null
  calendarEvent: CalendarEventStatus
  insurance: string | null
  customer: string | null
  rentalCar: string | null
  damagedCar: string | null
  customData: CalendarEventCustomData
} {
  // Parse date and time to timestamp
  const dateTime = new Date(`${form.date}T${form.time}:00`)
  const eventDate = dateTime.getTime()

  // End time is 1 hour later
  const endDate = eventDate + 60 * 60 * 1000

  return {
    eventDate,
    location: form.location || null,
    calendarEvent: mapStatusToBackend(form.type),
    insurance: form.insurance || null,
    customer: form.customerName || null,
    rentalCar: form.rentedCar || null,
    damagedCar: form.damagedCar || null,
    customData: {
      endDate
    }
  }
}

/**
 * Convert CalendarEvent to form data for editing
 */
export function mapEventToForm(event: CalendarEvent): CalendarEventForm {
  const startDate = new Date(event.eventDate)

  return {
    type: mapStatusToFrontend(event.calendarEvent),
    date: toDateString(startDate),
    time: toTimeString(startDate),
    location: event.location || '',
    customerName: event.customer || '',
    damagedCar: event.damagedCar || '',
    rentedCar: event.rentalCar || '',
    insurance: event.insurance || ''
  }
}

// ==================== Factory Functions ====================

/**
 * Create empty form with defaults
 */
export function createEmptyCalendarEventForm(): CalendarEventForm {
  return {
    type: 'delivery',
    date: toDateString(new Date()),
    time: '09:00',
    location: '',
    customerName: '',
    damagedCar: '',
    rentedCar: '',
    insurance: ''
  }
}
